import {DndContext, PointerSensor, useSensor, useSensors, type DragEndEvent} from '@dnd-kit/core';
import {SortableContext, arrayMove, rectSortingStrategy} from '@dnd-kit/sortable';
import {useTranslationContext} from 'platform/components';
import {Show} from 'platform/foundation';

import {useMemo, useState} from 'react';

import {isNotNil} from 'ramda-adjunct';

import {suffixTestId} from 'shared/web';

import {VIRTUAL_PRESET_ID} from '../../constants/VIRTUAL_PRESET_ID';
import {Preset} from '../../types/Api';
import {isPresetCustom} from '../../utils/isPresetCustom';
import {AddPresetTab} from './components/AddPresetTab/AddPresetTab';
import {PresetActionsMenu} from './components/PresetActionsMenu/PresetActionsMenu';
import {PresetButton} from './components/PresetButton/PresetButton';
import {SortablePresetButton} from './components/SortablePresetButton/SortablePresetButton';
import {PresetApiStateEnum} from './constants/PresetApiStateEnum';
import {usePresetApiState} from './hooks/usePresetApiState';
import {usePresetColorPreview} from './hooks/usePresetColorPreview';
import {usePresetRowCounts} from './hooks/usePresetRowCounts';
import {PresetsPanelRendererProps} from './types/PresetsPanelRendererProps';
import {DRAG_ACTIVATION_DISTANCE, makeVirtualRowCountEntry} from './utils/presetPanelConstants';

export function PresetsPanelRenderer(props: PresetsPanelRendererProps) {
  const t = useTranslationContext();

  const {
    apiState,
    copyState,
    isPresetChangeDisabled,
    updatePresetProxy,
    duplicatePresetProxy,
    deletePresetProxy,
    applyPresetToVirtualProxy,
    updatePresetPositionProxy,
    activatePresetProxy,
  } = usePresetApiState({
    updatePreset: props.updatePreset,
    duplicatePreset: props.duplicatePreset,
    deletePreset: props.deletePreset,
    applyPresetToVirtual: props.applyPresetToVirtual,
    updatePresetPosition: props.updatePresetPosition,
  });

  const activePresetId = (props.activePreset as Preset)?.id;

  // The pinned virtual ("Default") tab is active whenever no saved preset is active —
  // i.e. the active preset is the virtual one (it carries no `title`/`description`).
  const isVirtualActive = isNotNil(props.activePreset) && !isPresetCustom(props.activePreset);

  // Loading state for the pinned "Default" tab (set while switching back to the
  // virtual view); defaults to idle.
  const virtualState = apiState[VIRTUAL_PRESET_ID] ?? PresetApiStateEnum.idle;

  // Row counts are fetched for all presets across the wrapped rows.
  // The virtual ("Default") preset is included ONLY while it is inactive: when it is
  // active its count equals the grid's total count (props.activeViewRowCount), which is
  // already fetched — fetching it again would issue a duplicate /count for the same
  // dataQueryId.
  const rowCountItems = useMemo(() => {
    if (!props.virtualPreset || isVirtualActive) {
      return props.presets;
    }
    return [makeVirtualRowCountEntry(props.virtualPreset), ...(props.presets ?? [])];
  }, [props.presets, props.virtualPreset, isVirtualActive]);

  const rowCounts = usePresetRowCounts({
    visibleItems: rowCountItems,
    rowCountGetter: props.rowCountGetter,
  });
  const presetsRowCount = rowCounts.presetsRowCount;

  // Local order state for drag-and-drop — synced from props.presets
  const [localPresets, setLocalPresets] = useState<Preset[]>(props.presets ?? []);
  const [prevPresets, setPrevPresets] = useState(props.presets);

  if (prevPresets !== props.presets) {
    setPrevPresets(props.presets);
    setLocalPresets(props.presets ?? []);
  }

  const {resolveColor, setPreviewColor} = usePresetColorPreview(props.presets);

  const sensors = useSensors(
    useSensor(PointerSensor, {activationConstraint: {distance: DRAG_ACTIVATION_DISTANCE}})
  );

  const handleDragEnd = (event: DragEndEvent) => {
    if (isPresetChangeDisabled) {
      return;
    }
    const {active, over} = event;
    if (!over || active.id === over.id) {
      return;
    }
    const oldIndex = localPresets.findIndex((p) => p.id === active.id);
    const newIndex = localPresets.findIndex((p) => p.id === over.id);
    if (oldIndex === -1 || newIndex === -1) {
      return;
    }
    setLocalPresets((prev) => arrayMove(prev, oldIndex, newIndex));
    updatePresetPositionProxy(active.id as string, oldIndex, newIndex);
  };

  const renderPresetButton = (preset: Preset) => {
    const isActive = preset.id === activePresetId;
    const state = apiState[preset.id] ?? PresetApiStateEnum.idle;
    return (
      <SortablePresetButton
        sortableId={preset.id}
        color={resolveColor(preset.id, preset.color)}
        rowCount={presetsRowCount[preset.dataQueryId]}
        unsaved={props.isPresetChanged && isActive}
        key={preset.id}
        preset={preset}
        state={state}
        copyIsLoading={copyState[preset.id] === PresetApiStateEnum.loading}
        active={isActive}
        isDisabled={isPresetChangeDisabled && state !== PresetApiStateEnum.loading}
        onClick={() => {
          if (isActive || isPresetChangeDisabled || state === PresetApiStateEnum.loading) {
            return;
          }
          activatePresetProxy(preset.id, () => props.activatePreset(preset));
        }}
        data-testid={suffixTestId(`preset-${preset.title}`, props)}
        actions={(closeMenu) => (
          <PresetActionsMenu
            preset={preset}
            presetChanged={props.isPresetChanged}
            activePresetId={activePresetId}
            presetColor={resolveColor(preset.id, preset.color)}
            onColorChange={setPreviewColor}
            updatePreset={updatePresetProxy}
            deletePreset={deletePresetProxy}
            duplicatePreset={duplicatePresetProxy}
            applyPresetToVirtual={applyPresetToVirtualProxy}
            applyToDefaultState={copyState[preset.id]}
            onCopySuccess={closeMenu}
            applyToDefaultDisabled={isPresetChangeDisabled}
            resetOverrides={props.resetOverrides}
            renamePreset={props.renamePreset}
            testId={suffixTestId(`preset-${preset.title}`, props)}
            editDialogTestId={suffixTestId('editPreset', props)}
            dialogOptionsTestId={suffixTestId('editPreset', props)}
            dialogId="editPreset"
          />
        )}
      />
    );
  };

  return (
    <div className="flex flex-wrap w-full min-w-0 items-center gap-1.5 [&>*]:shrink-0">
      {/* Pinned virtual-preset ("Default") tab — always the leftmost tab. */}
      <Show when={props.virtualPreset}>
        {(virtualPreset) => (
          <PresetButton
            rowCount={
              isVirtualActive
                ? props.activeViewRowCount
                : presetsRowCount[virtualPreset.dataQueryId]
            }
            unsaved={false}
            preset={{
              id: VIRTUAL_PRESET_ID,
              title: t('page.datagrid.labels.defaultView'),
              description: '',
              dataQueryId: virtualPreset.dataQueryId,
              isActive: isVirtualActive,
            }}
            state={virtualState}
            active={isVirtualActive}
            isDisabled={isPresetChangeDisabled && virtualState !== PresetApiStateEnum.loading}
            onClick={() => {
              if (
                isVirtualActive ||
                isPresetChangeDisabled ||
                virtualState === PresetApiStateEnum.loading
              ) {
                return;
              }
              activatePresetProxy(VIRTUAL_PRESET_ID, () => props.deactivatePreset());
            }}
            data-testid={suffixTestId('preset-default', props)}
          />
        )}
      </Show>

      <DndContext sensors={sensors} onDragEnd={handleDragEnd}>
        <SortableContext items={localPresets.map((p) => p.id)} strategy={rectSortingStrategy}>
          {localPresets.map(renderPresetButton)}
        </SortableContext>
      </DndContext>

      <AddPresetTab
        isDisabled={isPresetChangeDisabled}
        onCreate={props.createPreset}
        data-testid={props['data-testid']}
      />
    </div>
  );
}
